import React, { useState, useEffect } from "react";
import { Modal, Button, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Select from "react-select";

const IBANKYCModal = ({ show, onHide, onSuccess, onError }) => {
  const { t } = useTranslation();
  
  // Personal Information
  const [personalInfo, setPersonalInfo] = useState({
    full_name: "",
    gender: "",
    date_of_birth: "",
    nationality: "",
    residential_address: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
    phone_code: "",
    phone_number: "",
    country_iso3: "",
    additional_info: ""
  });

  // Error states
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [userData, setUserData] = useState(null);
  const [agreementChecked, setAgreementChecked] = useState(false);

  const headers = {
    "Content-Type": "application/json",
    useraccesstoken: process.env.REACT_APP_USER_ACCESS_TOKEN,
    userauthtoken: "Bearer " + window.sessionStorage.getItem("user_jwt"),
  };

  // Helper function to format date for HTML input
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  useEffect(() => {
    if (show) {
      loadCountries();
      loadUserIBANKYCDetails();
    }
  }, [show]);

  const loadCountries = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}findallCountries`,
        { headers }
      );
      if (response.data.code === "200") {
        setCountries(response.data.data);
      }
    } catch (error) {
      console.error("Error loading countries:", error);
    }
  };

  const loadUserIBANKYCDetails = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}account/get-user-iban-kyc-details`,
        { headers }
      );
      
      if (response.data.code === "200" && response.data.data) {
        const data = response.data.data;

        // Populate personal info with default values
        setPersonalInfo(prev => ({
          ...prev,
          full_name: data.full_name || "",
          gender: data.gender || "",
          date_of_birth: formatDateForInput(data.date_of_birth),
          nationality: data.nationality || "",
          residential_address: data.residential_address || "",
          city: data.city || "",
          state: data.state || "",
          zip_code: data.zip_code || "",
          country: data.country || "",
          phone_code: data.phone_code || "",
          phone_number: data.phone_number || "",
          country_iso3: data.country_iso3 || "",
          additional_info: data.additional_info || ""
        }));

        // Store user data for rejection reason display
        setUserData(data);
      }
    } catch (error) {
      console.error("Error loading user IBAN KYC details:", error);
    }
  };

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleSelectChange = (field, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Personal Information validation
    if (!personalInfo.full_name) newErrors.full_name = "Full Name is required";
    if (!personalInfo.gender) newErrors.gender = "Gender is required";
    if (!personalInfo.date_of_birth) newErrors.date_of_birth = "Date of Birth is required";
    if (!personalInfo.nationality) newErrors.nationality = "Nationality is required";
    if (!personalInfo.residential_address) newErrors.residential_address = "Residential Address is required";
    if (!personalInfo.city) newErrors.city = "City is required";
    if (!personalInfo.state) newErrors.state = "State is required";
    if (!personalInfo.zip_code) newErrors.zip_code = "ZIP Code is required";
    if (!personalInfo.country) newErrors.country = "Country is required";
    if (!personalInfo.phone_code) newErrors.phone_code = "Phone Code is required";
    if (!personalInfo.phone_number) newErrors.phone_number = "Phone Number is required";
    if (!personalInfo.country_iso3) newErrors.country_iso3 = "Country ISO3 Code is required";

    // Agreement checkbox validation
    if (!agreementChecked) newErrors.agreement = "You must agree to the terms and conditions";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      
      // Personal Information
      formData.append('full_name', personalInfo.full_name);
      formData.append('gender', personalInfo.gender);
      formData.append('date_of_birth', personalInfo.date_of_birth);
      formData.append('nationality', personalInfo.nationality);
      formData.append('residential_address', personalInfo.residential_address);
      formData.append('city', personalInfo.city);
      formData.append('state', personalInfo.state);
      formData.append('zip_code', personalInfo.zip_code);
      formData.append('country', personalInfo.country);
      formData.append('phone_code', personalInfo.phone_code);
      formData.append('phone_number', personalInfo.phone_number);
      formData.append('country_iso3', personalInfo.country_iso3);
      formData.append('additional_info', personalInfo.additional_info || null);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}account/store-update-user-iban-kyc`,
        formData,
        { 
          headers: {
            "Content-Type": "multipart/form-data",
            useraccesstoken: process.env.REACT_APP_USER_ACCESS_TOKEN,
            userauthtoken: "Bearer " + window.sessionStorage.getItem("user_jwt"),
          }
        }
      );

      if (response.data.code === "200") {
        onSuccess(response.data.message);
        handleClose();
      } else {
        onError(response.data.message || "Something went wrong, please try again later.");
      }
    } catch (error) {
      console.error("Error submitting KYC form:", error);
      onError("Something went wrong, please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset all form data
    setPersonalInfo({
      full_name: "",
      gender: "",
      date_of_birth: "",
      nationality: "",
      residential_address: "",
      city: "",
      state: "",
      zip_code: "",
      country: "",
      phone_code: "",
      phone_number: "",
      country_iso3: "",
      additional_info: ""
    });
    setErrors({});
    setAgreementChecked(false);
    onHide();
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      size="lg" 
      centered 
      backdrop="static" 
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>IBAN KYC Information Form</Modal.Title>
        <button type="button" className="close" onClick={handleClose}>
          <span>&times;</span>
        </button>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {/* Show rejection reason if form is rejected */}
        {userData && userData.new_iban_form_status === 'Rejected' && userData.rejected_reason && (
          <div className="alert alert-danger mb-4" role="alert">
            <h6 className="alert-heading mb-2">
              <i className="fa fa-exclamation-triangle mr-2"></i>
              Application Rejected
            </h6>
            <p className="mb-0">
              <strong>Reason:</strong> {userData.rejected_reason}
            </p>
            <hr className="my-2" />
            <small className="text-muted">
              Please review and correct the issues mentioned above before resubmitting.
            </small>
          </div>
        )}

        <div className="row">
          {/* Personal Information Section */}
          <div className="col-12 mb-4">
            <h5 className="text-black font-w500 mb-3">Personal Information</h5>
            
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="mb-2">Full Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={personalInfo.full_name}
                    onChange={(e) => handlePersonalInfoChange("full_name", e.target.value)}
                    placeholder="Enter full name"
                  />
                  {errors.full_name && <small className="text-danger">{errors.full_name}</small>}
                </div>
              </div>
              
              <div className="col-md-6">
                <div className="form-group">
                  <label className="mb-2">Gender <span className="text-danger">*</span></label>
                  <div className="d-flex">
                    <div className="form-check mr-3">
                      <input
                        type="radio"
                        className="form-check-input"
                        name="gender"
                        value="Male"
                        checked={personalInfo.gender === "Male"}
                        onChange={(e) => handlePersonalInfoChange("gender", e.target.value)}
                      />
                      <label className="form-check-label">Male</label>
                    </div>
                    <div className="form-check mr-3">
                      <input
                        type="radio"
                        className="form-check-input"
                        name="gender"
                        value="Female"
                        checked={personalInfo.gender === "Female"}
                        onChange={(e) => handlePersonalInfoChange("gender", e.target.value)}
                      />
                      <label className="form-check-label">Female</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        name="gender"
                        value="Other"
                        checked={personalInfo.gender === "Other"}
                        onChange={(e) => handlePersonalInfoChange("gender", e.target.value)}
                      />
                      <label className="form-check-label">Other</label>
                    </div>
                  </div>
                  {errors.gender && <small className="text-danger">{errors.gender}</small>}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="mb-2">Date of Birth <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    value={personalInfo.date_of_birth}
                    onChange={(e) => handlePersonalInfoChange("date_of_birth", e.target.value)}
                  />
                  {errors.date_of_birth && <small className="text-danger">{errors.date_of_birth}</small>}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group position-relative">
                  <label className="mb-2">Nationality <span className="text-danger">*</span></label>
                  <Select
                    options={countries.map((country) => ({
                      value: country.sortname,
                      label: country.name,
                    }))}
                    placeholder="Select nationality"
                    isSearchable
                    className="react-select-container"
                    classNamePrefix="react-select"
                    name="nationality"
                    onChange={(selectedOption) => handleSelectChange("nationality", selectedOption)}
                    value={countries.find(country => country.sortname === personalInfo.nationality) ? {
                      value: personalInfo.nationality,
                      label: countries.find(country => country.sortname === personalInfo.nationality)?.name
                    } : null}
                  />
                  {errors.nationality && <small className="text-danger">{errors.nationality}</small>}
                </div>
              </div>

              <div className="col-md-12">
                <div className="form-group">
                  <label className="mb-2">Residential Address <span className="text-danger">*</span></label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={personalInfo.residential_address}
                    onChange={(e) => handlePersonalInfoChange("residential_address", e.target.value)}
                    placeholder="Enter residential address"
                  />
                  {errors.residential_address && <small className="text-danger">{errors.residential_address}</small>}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="mb-2">City <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={personalInfo.city}
                    onChange={(e) => handlePersonalInfoChange("city", e.target.value)}
                    placeholder="Enter city"
                  />
                  {errors.city && <small className="text-danger">{errors.city}</small>}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group position-relative">
                  <label className="mb-2">Country <span className="text-danger">*</span></label>
                  <Select
                    options={countries.map((country) => ({
                      value: country.sortname,
                      label: country.name,
                    }))}
                    placeholder="Select country"
                    isSearchable
                    className="react-select-container"
                    classNamePrefix="react-select"
                    name="country"
                    onChange={(selectedOption) => handleSelectChange("country", selectedOption)}
                    value={countries.find(country => country.sortname === personalInfo.country) ? {
                      value: personalInfo.country,
                      label: countries.find(country => country.sortname === personalInfo.country)?.name
                    } : null}
                  />
                  {errors.country && <small className="text-danger">{errors.country}</small>}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="mb-2">State <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={personalInfo.state}
                    onChange={(e) => handlePersonalInfoChange("state", e.target.value)}
                    placeholder="Enter state"
                  />
                  {errors.state && <small className="text-danger">{errors.state}</small>}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="mb-2">ZIP Code <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={personalInfo.zip_code}
                    onChange={(e) => handlePersonalInfoChange("zip_code", e.target.value)}
                    placeholder="Enter ZIP code"
                  />
                  {errors.zip_code && <small className="text-danger">{errors.zip_code}</small>}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group position-relative">
                  <label className="mb-2">Phone Code <span className="text-danger">*</span></label>
                  <Select
                    options={countries.map((country) => ({
                      value: country.phonecode,
                      label: `+${country.phonecode} ${country.name}`,
                    }))}
                    placeholder="Select phone code"
                    isSearchable
                    className="react-select-container"
                    classNamePrefix="react-select"
                    name="phone_code"
                    onChange={(selectedOption) => handleSelectChange("phone_code", selectedOption)}
                    value={countries.find(country => country.phonecode === personalInfo.phone_code) ? {
                      value: personalInfo.phone_code,
                      label: `+${personalInfo.phone_code} ${countries.find(country => country.phonecode === personalInfo.phone_code)?.name}`
                    } : null}
                  />
                  {errors.phone_code && <small className="text-danger">{errors.phone_code}</small>}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="mb-2">Phone Number <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={personalInfo.phone_number}
                    onChange={(e) => handlePersonalInfoChange("phone_number", e.target.value)}
                    placeholder="Enter phone number"
                  />
                  {errors.phone_number && <small className="text-danger">{errors.phone_number}</small>}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="mb-2">Country ISO3 Code <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={personalInfo.country_iso3}
                    onChange={(e) => handlePersonalInfoChange("country_iso3", e.target.value.toUpperCase())}
                    placeholder="e.g. USA, GBR, DEU"
                    maxLength={3}
                  />
                  {errors.country_iso3 && <small className="text-danger">{errors.country_iso3}</small>}
                </div>
              </div>

              <div className="col-md-12">
                <div className="form-group">
                  <label className="mb-2">Additional Information</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={personalInfo.additional_info}
                    onChange={(e) => handlePersonalInfoChange("additional_info", e.target.value)}
                    placeholder="Enter any additional information"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Agreement Declaration Section */}
          <div className="col-12 mb-4">
            <div className="card border-primary">
              <div className="card-body">
                <h6 className="card-title text-primary mb-3">
                  <i className="fa fa-file-text-o mr-2"></i>
                  Declaration and Agreement
                </h6>
                <div className="form-group mb-0">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="agreement"
                      checked={agreementChecked}
                      onChange={(e) => {
                        setAgreementChecked(e.target.checked);
                        if (errors.agreement) {
                          setErrors(prev => ({ ...prev, agreement: "" }));
                        }
                      }}
                    />
                    <label className="form-check-label" htmlFor="agreement">
                      <strong>I declare that the above information is true and accurate.</strong> I agree to provide any documents required for compliance purposes and understand that OCM Alinkos Group Ltd. may refuse to establish or terminate the relationship in case of missing or false information.
                      <span className="text-danger ml-1">*</span>
                    </label>
                  </div>
                  {errors.agreement && <small className="text-danger">{errors.agreement}</small>}
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="light" 
          className="btn-rounded" 
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          className="btn-rounded" 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Processing..." : "Submit"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default IBANKYCModal;